import ArrowLeftIcon from "@/assets/common/ic_arrow_left_bt.svg?react";
import ArrowRightIcon from "@/assets/common/ic_arrow_right_bt.svg?react";
import CloseIcon from "@/assets/common/ic_close_gray_secondary.svg?react";
import { useCallback, useEffect, useRef, useState } from "react";
import recordStore, { IMarkMap } from "@/store/record.store";
import summaryCommonStore from "@/store/summaryCommon.store";
import Mark from "mark.js";
import { debounce } from "@common/utils";
import './index.less';

export interface ISearchBarProps {
  className?: string;
  onCancel?: () => void;
  querySelector: string;
  trigger?: "input" | "enter";
  onSearch?: () => void;
  onJumpTo?: (index: number) => void;
}
const SearchBar: React.FC<ISearchBarProps> = (props) => {
  const {
    className = "",
    onCancel,
    onSearch,
    onJumpTo,
  } = props;
  const markRef = useRef<Mark | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalIndex, setTotalIndex] = useState(0);
  const markMapRef = useRef<IMarkMap>({});
  const styleRef = useRef<HTMLStyleElement | null>();

  const backTraceSearch = summaryCommonStore((state) => state.backTraceSearch);
  const searchValue = backTraceSearch;

  useEffect(() => {
    updateMark();
  }, [searchValue]);

  useEffect(() => {
    return () => {
      unmark();
      const style = styleRef.current;
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      } else {
        console.log("Style node not found");
      }
      styleRef.current = null;
    };
  }, []);

  const resetNum = useCallback(() => {
    setCurrentIndex(0);
    setTotalIndex(0);
  }, []);

  const unmark = () => {
    // 清空记录表
    markMapRef.current = {};
    const records = recordStore.getState().records;
    const newRecords = records.map((record) => {
      record.sc = record.sc.map((sItem) => {
        sItem.words = sItem.words.map((wItem) => {
          wItem.formatWord = "";
          return wItem;
        });
        return sItem;
      });
      return record;
    });
    recordStore.getState().setMarkMap({});
    recordStore.getState().setRecords(newRecords);
  };

  const updateMark = () => {
    unmark();
    // 记录在数据中所有标识分别在哪个段落, 然后根据定位到具体元素
    const records = recordStore.getState().records;
    const backTraceArr = summaryCommonStore.getState().backTraceArr;
    const markMap: IMarkMap = {};
    backTraceArr.forEach((item, index) => {
      markMap[index + 1] = {
        ...item,
        markId: `mark-${index + 1}`,
      };
    });

    const newRecords = records.map((record) => {
      const recordMarkArr = Object.values(markMap).filter(
        (item) => item.pi === record.pi
      );
      if (recordMarkArr?.length) {
        record.sc = record.sc.map((sItem) => {
          const recordMark = recordMarkArr.find(
            (item) => item.sIds.includes(sItem.si)
          );
          if (recordMark) {
            sItem.words = sItem.words.map((wItem) => {
              wItem.formatWord = `<mark data-mark-id="${recordMark.markId}" class="bg-[rgba(163,223,255,0.4)] ${recordMark.markId}">${wItem.word}</mark>`;
              return wItem;
            });
          }
          return sItem;
        });
      }
      return record;
    });

    markMapRef.current = markMap;
    recordStore.getState().setMarkMap(markMap);
    recordStore.getState().setRecords(newRecords);
    setTotalIndex(backTraceArr.length);
    if (backTraceArr.length < 1) {
      resetNum();
      return;
    }
    setCurrentIndex(1);
    jumpTo(1);
  };


  const debounceHandlePrev = debounce(() => {
    handlePrev();
  }, 200);
  const debounceHandleNext = debounce(() => {
    handleNext();
  }, 200);

  const updateStyle = (index: number) => {
    const css = `
      .mark-${index} {
        background: #A3DFFF
      }`;
    if (!styleRef.current) {
      const styleEle = document.createElement("style");
      styleEle.textContent = css;
      document.head.appendChild(styleEle);
      styleRef.current = styleEle;
    }
    styleRef.current.textContent = css;
  };

  const jumpTo = async (index: number) => {
    if (!Object.keys(markMapRef.current).length) return;
    // 使用 style节点来更新当前高亮元素
    updateStyle(index);
    onSearch && onSearch();

    const targetSen = markMapRef.current[index];
    if (!targetSen) return;
    const { pIndex, markId } = targetSen;
    if (pIndex < 0) return;

    // 跳转到目标段落
    onJumpTo && await onJumpTo(pIndex);

    const targetEle = document?.querySelector(
      `[data-mark-id="${markId}"]`
    );
    if (targetEle) {
      setTimeout(() => {
        targetEle.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 1) {
      jumpTo(currentIndex - 1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < totalIndex) {
      jumpTo(currentIndex + 1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleHideSearch = () => {
    markRef.current?.unmark();
    unmark();
    onCancel?.();
  };

  return (
    <div
      className={`
        ${className}
        min-w-60 h-8 rounded-2.5 
        flex items-center
      `}
    >
      <div className="mx-text-ellipsis flex-1 text-mx-primary font-500 pl-2.5 text-3.25 mr-2">{`回溯:${backTraceSearch}`}</div>
      <ArrowLeftIcon
        onClick={debounceHandlePrev}
        className={`size-4 mr-0.5 active:bg-#F4F6F9 active:rounded-1 arrow-icon ${(currentIndex === 0 || currentIndex === 1) ? 'disabled cursor-not-allowed' : 'cursor-pointer'}`}
      />
      <div className="text-3 text-mx-quaternary mx-0.5 min-w-5.5 font-400">
        <span className="text-mx-secondary">{currentIndex}</span>/
        <span>{totalIndex}</span>
      </div>
      <ArrowRightIcon
        onClick={debounceHandleNext}
        className={`size-4 ml-0.5 active:bg-#F4F6F9 active:rounded-1 arrow-icon ${currentIndex === totalIndex ? 'disabled cursor-not-allowed' : 'cursor-pointer'}`}
      />
      <div className="w-0.25 h-4 bg-#ECEEF3 ml-2" />
      <CloseIcon
        onClick={handleHideSearch}
        className="size-4 ml-2 mr-2 cursor-pointer"
      />
    </div>
  );
};

export default SearchBar;
